/**
 * Меню сотрудника на телефоне: 4 пункта внизу + «Ещё» (решение владельца 13.10.2026, docs/CURATOR-MOBILE-DESIGN.md §2).
 *
 * Прежде нижнее меню сотрудника было `items.slice(0, 5)` — первые пять пунктов бокового меню, а боковое меню на
 * телефоне скрыто. Куратору оставалось 5 разделов из 15, админу 5 из 34; профиля, языка и «Выйти» не было вовсе.
 *
 * ⚠️ Каждый пункт `NAV_BY_ROLE` сотрудника обязан стоять либо в доке, либо в группе «Ещё» — сторож
 * `app/staffNavReach.test.ts`. Пункт, которого здесь нет, всё равно не пропадёт: `splitStaffNav` складывает такие
 * в группу «Другое» — но сторож краснеет, чтобы место ему выбрали явно.
 */

export type StaffRole = 'curator' | 'admin';

/** Четыре пункта нижнего меню, по частоте с телефона (сверка 12.10.2026, §3); пятый — «Ещё». */
export const STAFF_DOCK: Record<StaffRole, readonly string[]> = {
  curator: ['/curator', '/curator/students', '/curator/homework', '/curator/chat'],
  admin: ['/admin', '/admin/students', '/admin/marathons', '/admin/homework'],
};

export const MORE_PATH: Record<StaffRole, string> = {
  curator: '/curator/more',
  admin: '/admin/more',
};

/** Профиль живёт в блоке «Аккаунт», а не в разделах. У админа профиля нет. */
export const PROFILE_PATH: Partial<Record<StaffRole, string>> = {
  curator: '/curator/profile',
};

export type MoreGroup = { labelKey: string; paths: readonly string[] };

export const MORE_GROUPS: Record<StaffRole, readonly MoreGroup[]> = {
  curator: [
    {
      labelKey: 'staffMore.work',
      paths: [
        '/curator/notifications',
        '/curator/groups',
        '/curator/flagged-works',
        '/curator/schedule',
        '/curator/speaking-rooms',
      ],
    },
    {
      labelKey: 'staffMore.content',
      paths: [
        '/curator/daily-tasks',
        '/curator/trainer-schedule',
        '/curator/cue-cards',
        '/curator/materials',
        '/curator/stories',
      ],
    },
  ],
  admin: [
    {
      labelKey: 'staffMore.people',
      paths: ['/admin/curators', '/admin/groups', '/admin/imports', '/admin/leads', '/admin/trash', '/admin/devices'],
    },
    {
      labelKey: 'staffMore.study',
      paths: [
        '/admin/schedule',
        '/admin/speaking-rooms',
        '/admin/flagged-works',
        '/admin/daily-tasks',
        '/admin/trainer-schedule',
        '/admin/cue-cards',
        '/admin/materials',
        '/admin/stories',
        '/admin/chats',
      ],
    },
    {
      labelKey: 'staffMore.ieltsAi',
      paths: [
        '/admin/ielts-writing',
        '/admin/ielts-speaking',
        '/admin/ielts-reading',
        '/admin/ielts-listening',
        '/admin/ielts-pool',
        '/admin/ai-pipeline',
        '/admin/trainer-pool',
      ],
    },
    {
      labelKey: 'staffMore.platform',
      paths: [
        '/admin/app-config',
        '/admin/notification-templates',
        '/admin/platform-health',
        '/admin/server',
        '/admin/app-crashes',
        '/admin/stats',
        '/admin/recordings',
        '/admin/preview',
      ],
    },
  ],
};

/**
 * Подсвечивать ли «Ещё» в нижнем меню: открыт сам экран «Ещё» или раздел из его групп (вместе с вложенными адресами —
 * `/curator/groups/:id`), профиль тоже. Решение владельца 13.10.2026: прежде, пока открыт раздел из «Ещё», в нижнем
 * меню не горел ни один пункт.
 */
export function isUnderMore(role: StaffRole, pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '');
  const under = (p: string) => path === p || path.startsWith(`${p}/`);
  if (under(MORE_PATH[role])) return true;
  const profile = PROFILE_PATH[role];
  if (profile && under(profile)) return true;
  return MORE_GROUPS[role].some((g) => g.paths.some(under));
}

/**
 * Делит уже отфильтрованные пункты меню (роль, выключатели, приложение — правила оболочки) на док и группы «Ещё».
 * Порядок внутри группы — как в `MORE_GROUPS`; пустые группы не возвращаются.
 */
export function splitStaffNav<T extends { to: string }>(
  role: StaffRole,
  items: readonly T[],
): { dock: T[]; groups: { labelKey: string; items: T[] }[]; profile?: T } {
  const byPath = new Map(items.map((i) => [i.to, i]));
  const dock = STAFF_DOCK[role].map((p) => byPath.get(p)).filter((i): i is T => Boolean(i));
  const placed = new Set<string>([...STAFF_DOCK[role], ...(PROFILE_PATH[role] ? [PROFILE_PATH[role]!] : [])]);
  const groups = MORE_GROUPS[role].map((g) => {
    g.paths.forEach((p) => placed.add(p));
    return { labelKey: g.labelKey, items: g.paths.map((p) => byPath.get(p)).filter((i): i is T => Boolean(i)) };
  });
  const rest = items.filter((i) => !placed.has(i.to));
  if (rest.length) groups.push({ labelKey: 'staffMore.other', items: rest });
  const profilePath = PROFILE_PATH[role];
  return {
    dock,
    groups: groups.filter((g) => g.items.length > 0),
    profile: profilePath ? byPath.get(profilePath) : undefined,
  };
}
